import { SocketContext } from "@/Context/SocketContext";
import { Settings } from "@ailenax-overlay/shared";
import { useContext, useEffect, useState } from "react";

export type UseSettingReturnType<T extends Settings["name"], S extends Extract<Settings, { name: T }>> = [
    S["type"] | undefined,
    (value: S["type"] | ((prev: S["type"] | undefined) => S["type"])) => void];

export const useSetting = <T extends Settings["name"], S extends Extract<Settings, { name: T }>>(name: T): UseSettingReturnType<T, S> => {
    const [value, setValue] = useState<S["type"]>(undefined);
    const socket = useContext(SocketContext);

    useEffect(() => {
        // @ts-expect-error - This is a valid event, but TypeScript can't infer it properly
        socket.emit(`get-setting-${name}`, (value) => {
            console.log(`Received initial value for setting ${name}:`, value);
            setValue(value);
        });

        const handler = (e: S["update-event"]) => {
            console.log(`Received update for setting ${name}:`, e.value);
            setValue(e.value);
        }

        // @ts-expect-error - This is a valid event, but TypeScript can't infer it properly
        socket.on(`setting-update-${name}`, handler);

        return () => {
            // @ts-expect-error - This is a valid event, but TypeScript can't infer it properly
            socket.off(`setting-update-${name}`, handler);
        }
    }, [socket, name]);

    const setSetting: UseSettingReturnType<T, S>[1] = (v) => {
        const newValue = typeof v === "function" ? v(value) : v;
        setValue(newValue);
        // @ts-expect-error - This is a valid event, but TypeScript can't infer it properly
        socket.emit(`set-setting-${name}`, newValue);
    }

    return [value, setSetting];
}